import moment from 'moment/moment';
import { ACCESS_TOKEN, ACCESS_TOKEN_EXPIRY_TIME, GUESS_TOKEN, LOCAL_STORAGE, REFRESH_TOKEN, REFRESH_TOKEN_EXPIRY_TIME } from '../assets/constants/BrowserStorageKeys';
import { API_TIMEOUT_FOR_CLOSE_DOOR_API, ASSIGNMENT_SLUG, DEVICE_ID, DEVICE_TYPE, PHONE_PREFIX, PWA, TRUSTED_LINKING_TOKEN, TRUSTED_LINKING_TOKEN_VAL } from '../assets/constants/Constants';
import { getSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import axiosInstance from './axiosInstance';
import { convertSecondsToDateTimeFormat} from '../lib/Utils';
import masterAxiosInstance from './masterAxiosInstance';

export const getTenantAndAssignmentSettings = async (lockerBankId) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
        }
        const response = await axiosInstance.get(`${PWA}/bank/${lockerBankId}/details/`,{headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to fetch details for locker bank ID ${lockerBankId}:`, error);
        throw error;
    }
};

export const sendOtp = async (emailOrMobile, assignmentSlug, isResendOtp) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG] : assignmentSlug
        }
        let body = {
            email_or_mobile: emailOrMobile,
        }

        if (isResendOtp) {
            body.is_resend = true;
        }
        const response = await axiosInstance.post(`${PWA}/auth/send-otp/`, body, { headers: headers });
        return response?.data;
    } catch (error) {
        console.error(`Failed to send OTP to phone number ${emailOrMobile}:`, error);
        throw error;
    }
};

export const verifyOtp = async (txnId, otp) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
        }
        let body = {
            txn_id: txnId,
            otp: otp
        }
        const response = await axiosInstance.post(`${PWA}/auth/verify-otp/`, body, { headers: headers });
        return response?.data;
    } catch (error) {
        console.error(`Failed to send OTP to phone number ${txnId}:`, error);
        throw error;
    }
};

export const verifyWithUserNamePassword = async (body) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
        }
       
        const response = await axiosInstance.post(`${PWA}/auth/verify-password/`, body, { headers: headers });
        return response?.data;
    } catch (error) {
        console.error(`Failed to Verify the User with Username and password:`, error);
        throw error;
    }
};

export const linkMyDevice = async (encryptedPayload, deviceUniqueId) => {
    try {
        const headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL
        };

        const body = {
            encrypted_payload: encryptedPayload,
            device_unique_id: deviceUniqueId,
        };

        const response = await axiosInstance.post(`${PWA}/auth/link-my-device/`, body, {headers});
        return response?.data;
    } catch (error)  {
        console.error(`Error during link my device: `, error);
        throw error;
    }
}

export const getDomainAndLockerBankIdFromShortCode = async (lockerBankCode) => {
    try {
        const headers= {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
        }
        const response = await axiosInstance.get(`configurations/${lockerBankCode}/bank-identifier/`,{baseURL: process.env.REACT_APP_MASTER_DOMAIN, headers: headers}
        );
        return response?.data;
    } catch (error) {
        console.error(`Failed to get domain and locker bank ID for short code ${lockerBankCode}:`,error);
        throw error;
    }
};

export const getAvailableLockerSizesWithUnits = async (assignmentSlug, lockerBankId) => {
    try {
            const headers = {
                [ASSIGNMENT_SLUG]: assignmentSlug
            };
            const response = await axiosInstance.get(`${PWA}/bank/available-sizes/?locker_bank_id=${lockerBankId}`, {headers: headers});
            return response?.data;
    } catch (error) {
        console.error('Failed to get available locker sizes with units: ', error);
        throw error;
    }
};

export const reserveLocker = async (assignmentSlug, lockerBankId, userId, selectedSize = null, selectedUnit = null, bookingSlotId = null, paymentAmount = null) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG]: assignmentSlug
            };
            const body = {
                locker_bank_id: lockerBankId,
                user_id: userId
            };
            if (selectedSize) {
                body.size = selectedSize;
            }
            if (selectedUnit) {
                body.door_id = selectedUnit;
            }
            if (bookingSlotId) {
                body.door_size_payment_slab = bookingSlotId;
            }
            if (paymentAmount) {
                body.payment_amount = paymentAmount;
            }
            const response = await axiosInstance.post(`${PWA}/bank/reserve/`, body, {headers: headers});
            return response?.data;
        }
    } catch (error) {
        console.error('Failed to reserve locker: ', error);
        throw error;
    }
};

export const getAssignedLockerList = async (assigmentKey, searchText,page) => {
    try {
        let queryParams = `?assignment_id=${assigmentKey}` + (searchText ? `&search=${searchText}` : '' + (page > 1 ? `&page=${page}` : ''));
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
             const response = await axiosInstance.get(`${PWA}/bank/my-lockers/`, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        const { status, data, headers } = error?.response;
        switch (status) {
            case 400: Object.assign(error.response);
            break;
            default: throw error;
        }
    }
};

export const getRefreshedAccessToken = async () => {
    try {
        const accessToken = await getSecureItemFromSpecificStorage(LOCAL_STORAGE, ACCESS_TOKEN);
        const refreshToken = await getSecureItemFromSpecificStorage(LOCAL_STORAGE, REFRESH_TOKEN);
        const accessExpiryAt = await getSecureItemFromSpecificStorage(LOCAL_STORAGE, ACCESS_TOKEN_EXPIRY_TIME);
        const refreshExpiryAt = await getSecureItemFromSpecificStorage(LOCAL_STORAGE, REFRESH_TOKEN_EXPIRY_TIME);
        if (!accessToken || !refreshToken) {
            console.error('Token data is missing');
            return null;
        }
        const now = moment.utc();
        const tokenExpiryTime = moment.utc(accessExpiryAt);
        if (now.isBefore(tokenExpiryTime)) {
            return accessToken;
        } else {
            let headers = {
                [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
            }
            let body = {
                refresh_token: refreshToken
            }
            const response = await axiosInstance.post(`${PWA}/auth/refresh-token/`, body, { headers: headers });
            if (response) {
                const newAccessToken = response.data.data.access;
                const newAccessExpiryAt = response.data.data.access_expires_in;
                storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, ACCESS_TOKEN, newAccessToken);
                storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, ACCESS_TOKEN_EXPIRY_TIME, convertSecondsToDateTimeFormat(newAccessExpiryAt));
                return newAccessToken;
            }
        }
    } catch (error) {
        console.error('Error refreshing access token:', error);
        return null;
    }
};


export const openLocker = async (refId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                ref_id: refId
            }
            const response = await axiosInstance.post(`${PWA}/bank/open-door/`,body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch locker list :`, error);
        throw error;
    }
};

export const dropParcel = async (refId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                ref_id: refId
            }
            const response = await axiosInstance.post(`${PWA}/bank/drop/`,body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch locker list :`, error);
        throw error;
    }
}

export const releaseLockerOfPickUpUser = async (refId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                ref_id: refId
            }
            const response = await axiosInstance.post(`${PWA}/bank/release/`,body, { headers: headers });
            return response?.data; 
        }
    } catch (error) {
        console.error(`Failed to fetch locker list :`, error);
        throw error;
    }
}

export const deleteUserAccount = async () => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axiosInstance.post(`${PWA}/auth/delete-user-account/`,{}, { headers: headers });
            return response?.data;

        }
    } catch (error) {
        console.error(`Failed to fetch locker list :`, error);
        throw error;
    }
}

export const cancelDropOffTransaction = async (refId,cancelReason) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                ref_id: refId,
                remark: cancelReason
            }
            const response = await axiosInstance.post(`${PWA}/bank/cancel/`,body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to cancel locker list :`, error);
        throw error;
    }
}

export const fetchPaymentStatus = async (refId,paymentGateway) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                payment_reference_id : refId,
                payment_gateway: paymentGateway
            }
            const response = await axiosInstance.post(`payment-gateway/fetch-payment/`,body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch payment status :`, error);
        throw error;
    }
}

/**
 * Creates an overdue payment transaction.
 *
 * `overdueRef` is used by overstay payments to store the rounded total duration
 * covered by the payment, formatted as a duration string such as "5:00:00".
 * Other payment flows can omit it.
 *
 * @param {string} transactionId Locker transaction reference ID.
 * @param {number|string} amount Amount to charge.
 * @param {string} overdueType Overdue calculation method.
 * @param {string} paymentType Payment type sent to the backend.
 * @param {string|null} overdueRef Rounded covered duration for overstay payments.
 * @returns {Promise<Object|undefined>} API response payload.
 */
export const overduePayment = async (transactionId, amount, overdueType='null',paymentType = 'OVERDUE', overdueRef = null) => {
    
    try {
        const accessToken = await getRefreshedAccessToken();

        if (accessToken) {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };

            const body = {
                transaction_id: transactionId,
                payment_type: paymentType,
                amount,
                overdue_type:overdueType
            };

            if (overdueRef !== null && overdueRef !== undefined) {
                body.overdue_ref = overdueRef;
            }
            const response = await axiosInstance.post('payment-gateway/overdue-payment/', body, {headers: headers});
            return response?.data;
        }
    } catch (error) {
        console.error('Error during overdue payment: ', error);
        throw error;
    }
};

export const getProductsList = async (assignmentSlug) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG] : assignmentSlug
            }
            const response = await axiosInstance.get(`${PWA}/inventory/product-list/`, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch payment status :`, error);
        throw error;
    }
}

export const rentProductAndReserveLocker = async (assignmentSlug,productId, productSlabId, paymentReferenceId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG] : assignmentSlug
            }

            const body = {
                    "product": productId,
                    "price_slab_id": productSlabId,
                    "payment_reference_id": paymentReferenceId
            }
            const response = await axiosInstance.post(`${PWA}/inventory/product-rent-out/`, body,{ headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch payment status :`, error);
        throw error;
    }
}

export const openLockerDoor = async (refId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                [DEVICE_ID] : "123445689", //Setting temporary device id, will be replaced with actual device id
                [DEVICE_TYPE] : PWA
            }

            const body = {
                   ref_id : refId
            }
            const response = await axiosInstance.post(`open-api/operations/open-door/`, body,{ headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to open door :`, error);
        throw error;
    }
}

export const registerGoogleLoginUser = async (googleAuthToken,assignmentSlug,email) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG]: assignmentSlug
        }

        const body = {
            token: googleAuthToken,
            email: email
        }
        const response = await axiosInstance.post(`${PWA}/auth/google-login/`, body, { headers: headers });
        return response?.data;

    } catch (error) {
        console.error(`Failed to register google login user :`, error);
        throw error;
    }
}

export const registerFacebookLoginUser = async (faceBookAuthToken,assignmentSlug,email) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN]: TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG]: assignmentSlug
        }

        const body = {
            token: faceBookAuthToken,
            email: email
        }
        const response = await axiosInstance.post(`${PWA}/auth/facebook-login/`, body, { headers: headers });
        return response?.data;

    } catch (error) {
        console.error(`Failed to register facebook login user :`, error);
        throw error;
    }
}

export const handleNotifyUserForProduct = async (assignmentSlug,productId) => {
    const accessToken = await getRefreshedAccessToken();
    try {
        let headers = {
            Authorization: `Bearer ${accessToken}`,
            [ASSIGNMENT_SLUG]: assignmentSlug
        }

        const body = {
            product: productId,
        }
        const response = await axiosInstance.post(`${PWA}/inventory/notify-me/`, body, { headers: headers });
        return response?.data;

    } catch (error) {
        console.error(`Failed to notify user :`, error);
        throw error;
    }
}

export const getSessionStatus = async (paymentGatewayReference, paymentReferenceId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(accessToken){
            let headers = {
                Authorization: `Bearer ${accessToken}`,
            }

            const body = {
                payment_gateway_reference : paymentGatewayReference,
                payment_reference_id:paymentReferenceId
            }
            const response = await axiosInstance.post(`payment-gateway/stripe/session-status/`, body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Error in getSessionStatus API :`, error);
        throw error;
    }
}

export const capturePayment = async (paymentReferenceId, finalAmount) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            let headers = {
                Authorization: `Bearer ${accessToken}`,
            }

            const body = {
                payment_reference_id: paymentReferenceId,
                amount : parseFloat(finalAmount)
            }
            const response = await axiosInstance.post(`payment-gateway/stripe/payment-charge/`, body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Error in capturePayment API :`, error);
        throw error;
    }
}

export const productRentInitiate = async (assignmentSlug, productId) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG] : assignmentSlug
            }

            const body = {
                product: productId,
            }
            const response = await axiosInstance.post(`${PWA}/inventory/product-rent-initiate/`, body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Error in productRentInitiate API :`, error);
        throw error;
    }
}

export const productPurchase = async (assignmentSlug, productId, amount) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            let headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG] : assignmentSlug
            }

            const body = {
                product: productId,
                amount: amount,
            }
            const response = await axiosInstance.post(`${PWA}/inventory/product-purchase/`, body, { headers: headers });
            return response?.data;
        }
    } catch (error) {
        console.error(`Error in productPurchase API :`, error);
        throw error;
    }
}

export const checkClosedLockerDoorStatus = async (refId, status) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            let headers = {
                Authorization: `Bearer ${accessToken}`
            }
            let body = {
                ref_id: refId,
                status: status
            }
            const response = await axiosInstance.post(`${PWA}/bank/check-door-status/`, body, { headers: headers, timeout: API_TIMEOUT_FOR_CLOSE_DOOR_API });
            return response?.data;
        }
    } catch (error) {
        console.error(`Failed to fetch payment status :`, error);
        throw error;
    }
}

export const searchParcelOwner = async (assignmentSlug ,query ) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG] : assignmentSlug
        }
        const response = await axiosInstance.get(`${PWA}/auth/search-user/?search=${query}`,{headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to fetch details for locker bank ID`, error);
        throw error;
    }
};

export const assignLockerToPickUpUser = async (assignmentSlug, lockerBankId, userId, selectedSize = null, selectedUnit = null) => {   
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG]: assignmentSlug
            };
            const body = {
                locker_bank_id: lockerBankId,
                user_id: userId
            };
            if (selectedSize) {
                body.size = selectedSize;
            }
            if (selectedUnit) {
                body.door_id = selectedUnit;
            }
            const response = await axiosInstance.post(`${PWA}/bank/reserve/`, body, {headers: headers});
            return response?.data;
        }
    } catch (error) {
        console.error('Failed to reserve locker: ', error);
        throw error;
    }
}

export const AnonymousUserAuthentication = async (assignmentSlug) => {  
    try {
        const headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG]: assignmentSlug
        }
        const response = await axiosInstance.get(`${PWA}/auth/get-anon/`, {headers: headers});
        return response?.data;
    } 
    catch (error) {
        console.error('Failed to login with Anonymous User: ', error);
        throw error;
    }
}

export const CreateParcelOwner = async (assignmentSlug ,data ) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG] : assignmentSlug,
        }
        let body = {...data};
        const response = await axiosInstance.post(`${PWA}/auth/get-or-create/`, body, {headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to fetch details for locker bank ID`, error);
        throw error;
    }
};

export const PickUpDoorVerification = async (assignmentSlug ,data) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG] : assignmentSlug,
        }
        let body = {...data};
        const response = await axiosInstance.post(`${PWA}/auth/door-otp/`, body, {headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to fetch details for locker bank ID`, error);
        throw error;
    }
};

export const DoorResendOTP = async (assignmentSlug ,data) => {
    try {
        let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
            [ASSIGNMENT_SLUG] : assignmentSlug,
        }
        let body = {...data};
        const response = await axiosInstance.post(`${PWA}/auth/resend-door-otp/`, body, {headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to Resend the OTP`, error);
        throw error;
    }
};

/*
api for gnerating the gest user token 
*/

export const generateGuestToken = async () => {
    try {
        const response = await masterAxiosInstance.post(`api/${PWA}/auth/guest-token/`);
        return response?.data;
    } catch (error) {
        console.error(`Failed to Generate the Gest Token`, error);
        throw error;
    }
};

/*
api for getting the locker details near to user based on lag, and lng and distance

*/

export const getLockerDetailsNearToUser = async (data) => {
    try {

        const accessToken = data?.token;

        if (accessToken) {

            const headers = {
                Authorization: `Bearer ${accessToken}`
            };

            const { lat, lng } = data.location;
            const body = {
                distance: data.distance
            };

            const response = await masterAxiosInstance.post(`api/${PWA}/bank/bank-list/?location=${lat},${lng}`,
                body, 
                { headers }
            );

            return response;

        }
    } catch (error) {
        console.error("Failed to get lockers near user:", error);
        throw error;
    }
};

export const advanceBookingLockerBankDetails = async (data) => {
  try {


    const accessToken = getSecureItemFromSpecificStorage(LOCAL_STORAGE, GUESS_TOKEN);

    if (!accessToken) {
      console.error("No access token provided");
      return null;
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`
    };

    const {
      lockerBankId,
      doorNumber,
      doorSize,
      doorStatus,
      reserve_start,
      reserve_end
    } = data;
    
    const body = {
     reserve_start:reserve_start,
    reserve_end: reserve_end
    };

    let url = `${PWA}/advance-booking/bank-details/${lockerBankId}/`;

    const queryParams = [];

    if (doorNumber) queryParams.push(`door_no=${doorNumber}`);
    if (doorSize) queryParams.push(`door_size=${doorSize}`);
    if (doorStatus) queryParams.push(`door_status=${doorStatus}`);

    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    const response = await axiosInstance.post(
      url,
      body,
      { headers }
    );

    return response;

  } catch (error) {
    console.error("Advance booking API failed:", error);
    throw error;
  }
};


export const advancereserveLocker = async (requestData) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG]: requestData?.assignmentSlug
            };
            
            const { lockerBankId, locker_bank_id, ...rest } = requestData || {};
            const body = {
                ...requestData
            }
            
            const response = await axiosInstance.post(`${PWA}/advance-booking/reserve/`, body, {headers: headers});
            return response;
        }
    
    } catch (error) {
        console.error('Failed to reserve advance locker: ', error);
        throw error;
    }
};



export const bulkLockerReserver = async (requestData) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if (accessToken) {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                [ASSIGNMENT_SLUG]: requestData?.assignmentSlug
            };
            
            const { lockerBankId, locker_bank_id, ...rest } = requestData || {};
            const body = {
                ...requestData,
            }
            const response = await axiosInstance.post(
                `${PWA}/bank/bulk-reserve/`,
                body,
                { headers }
            );

            return response;
        }

    } catch (error) {
        console.error('Failed to reserve bulk lockers:', error);
        throw error;
    }
};

export const buildBulkPaymentInitial = async (requestDataList) => {
  try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const body = {
      ...requestDataList 
    };


    const response = await axiosInstance.post(`${PWA}/advance-booking/initiate-payment/`,
      body,
      { headers }
    );

    return response;
  } catch (error) {
    console.error("Failed to initiate bulk payment:", error);
    throw error;
  }
};


export const getBulkAdvanceBookingAssignLockers = async () => {
  try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };

    const response = await axiosInstance.get(`${PWA}/advance-booking/list/`,
        { headers }
    );

    return response;

  } catch (error) {
    console.error(
      "Failed to fetch bulk advance booking assigned lockers:",
      error
    );
    throw error;
  }
};



export const CancelAdvanceBookingAssignLockers = async (requestData) => {
  try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...requestData
    }

    const response = await axiosInstance.post(`${PWA}/advance-booking/cancel/`,
        body,
        { headers }
    );

    return response;

  } catch (error) {
    console.error(
      "Failed to fetch bulk advance booking assigned lockers:",
      error
    );
    throw error;
  }
};


 export const userHistoryLockerDetails = async()=>{
    try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };


    const response = await axiosInstance.get(`${PWA}/bank/transaction-history/`,
        { headers }
    );

    return response;

  } catch (error) {
    console.error(
      "Failed to fetch the locker history",
      error
    );
    throw error;
  }
}
export const UpdateUserProfile = async (data) => {
    try {
        const accessToken = await getRefreshedAccessToken();
        if(!accessToken){
            throw new Error('No access token available');
        }
        let headers = {
            Authorization: `Bearer ${accessToken}`
        }
        let body = {...data};
        body.mobile_number = PHONE_PREFIX + body.mobile_number; 
        const response = await axiosInstance.patch(`${PWA}/auth/update-profile/`, body, {headers : headers});
        return response?.data;
    } catch (error) {
        console.error(`Failed to Update the User Profile`, error);
        throw error;
    }
};

export const earlyCheckInAdvance = async (requestData) => {
  try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...requestData
    }

    const response = await axiosInstance.post(`${PWA}/advance-booking/convert/`,
        body,
        { headers }
    );

    return response;

  } catch (error) {
    console.error(
      "Failed to conert advance booking to regular booking:",
      error
    );
    throw error;
  }
};

export const DoorDetails = async (requestData) => {
  try {
    const accessToken = await getRefreshedAccessToken();

    if (!accessToken) {
      throw new Error("Access token not available");
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const body = {
        ...requestData
    }

    const response = await axiosInstance.post(`${PWA}/bank/door-details/`,
        body,
        { headers }
    );

    return response?.data;

  } catch (error) {
    console.error(
      "Failed to fetch the Door details",
      error
    );
    throw error;
  }
};

export const getLockerScheduleDetails = async (lockerId, date) => {
  try {


    let headers = {
            [TRUSTED_LINKING_TOKEN] : TRUSTED_LINKING_TOKEN_VAL,
        }
    const params = {
      locker_id: lockerId,
      date: date,
    };

    const response = await axiosInstance.get(`${PWA}/bank/booking-details/`, { headers: headers, params: params });

    return response?.data;

  } catch (error) {
    console.error(
      "Failed to fetch the locker schedule details",
      error
    );
    throw error;
  }
};
